import { KnowledgeSource } from '@vendasta/embeddings';
import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import { from, Observable } from 'rxjs';

import { AiKnowledgeService } from '../services/ai-knowledge.service';
import { KnowledgeRowMapper } from './knowledge-row.mapper';
import { StaticTableApi } from './static-table.api';
import { TableKnowledgeSource } from './types';

/** A loaded page of sources surfaced back to the host so it can wire the source table's per-page inputs. */
export interface KnowledgeSourcePage {
  /** Raw sources by id — backs the source table's edit / refresh / delete for the visible rows. */
  rawById: Map<string, KnowledgeSource>;
  /** Mapped rows — backs the source table's legacy deep-link lookup. */
  rows: TableKnowledgeSource[];
}

/**
 * Server-backed paginated API for the Knowledge Sources table: the table's page size, cursor, and search
 * term drive the `listKnowledgeSources` query directly (no fetch-all). The endpoint has no sort. In
 * storybook / @Input mode (canned rows present) it falls back to in-memory paging.
 *
 * Each server page is published via `onPageLoaded` so the host can keep the source table's `rawSourcesById`
 * (edit/refresh/delete) and `rows` (deep-link) inputs in sync with what's actually shown.
 */
export class KnowledgeSourceTableApi implements PaginatedAPIInterface<TableKnowledgeSource, unknown, unknown> {
  private readonly staticApi = new StaticTableApi<TableKnowledgeSource>(
    () => this.cannedRows() ?? [],
    (ks) => `${ks.name} ${ks.subtitle} ${ks.description} ${ks.managedBy}`,
  );

  constructor(
    private readonly knowledgeService: AiKnowledgeService,
    private readonly mapper: KnowledgeRowMapper,
    /** Canned rows for storybook / @Input mode, or null to fetch from the server. */
    private readonly cannedRows: () => TableKnowledgeSource[] | null,
    /** Publishes the loaded page's raw sources + rows for the host's per-page source-table inputs. */
    private readonly onPageLoaded: (page: KnowledgeSourcePage) => void,
    /** Synthetic Business Profile source to prepend on the first page when the BE hasn't materialized it (BC), or null. */
    private readonly firstPageBusinessProfile: () => Promise<KnowledgeSource | null>,
  ) {}

  get(req: PagedListRequestInterface<unknown, unknown>): Observable<PagedResponseInterface<TableKnowledgeSource>> {
    if (this.cannedRows() !== null) {
      return this.staticApi.get(req);
    }
    return from(this.fetchPage(req));
  }

  private async fetchPage(
    req: PagedListRequestInterface<unknown, unknown>,
  ): Promise<PagedResponseInterface<TableKnowledgeSource>> {
    const pageSize = req.pagingOptions?.pageSize ?? 20;
    const cursor = req.pagingOptions?.cursor ?? '';
    const searchTerm = (req.searchOptions?.text ?? '').trim();
    const callerNs = this.knowledgeService.buildNamespaceInterface();
    const page = await this.knowledgeService.listKnowledgeSourcesPage(searchTerm, cursor, pageSize);
    const sources = [...page.knowledgeSources];

    // First unfiltered page only: surface the virtual Business Profile source when the BE hasn't
    // materialized it yet. Skipped during search — it's a local synthetic row the server can't match.
    if (!cursor && !searchTerm) {
      const bp = await this.firstPageBusinessProfile();
      if (bp && !sources.some((s) => s.id === bp.id || !!s.config?.businessProfileConfig)) {
        sources.unshift(bp);
      }
    }

    const rawById = new Map<string, KnowledgeSource>();
    for (const ks of sources) {
      if (ks.id) rawById.set(ks.id, ks);
    }
    const rows = await Promise.all(sources.map((ks) => this.mapper.toKsRow(ks, callerNs)));
    this.onPageLoaded({ rawById, rows });
    return {
      data: rows,
      pagingMetadata: { nextCursor: page.nextCursor, hasMore: page.hasMore, totalResults: page.totalResults },
    };
  }
}
